import { Price, Seat, SeatType } from "@/graphql/types";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "../common/modal/modal";
import Button from "../common/button/button";
import { numberToCharacter } from "../common/commonObject";
import GuestInput from "../common/input/guestInput";
import { Device, isMobile } from "@/lib/ts/device";
import SeatsDisplay from "../seat/seatsDisplay";
import { useQuery } from "react-query";
import { FilledInput, InputLabel, TextField } from "@mui/material";
import * as GraphQLClient from "@apollo/client";
import { CreateSeatsFromCsv } from "@/graphql/documentNode";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import SelectItemV2 from "../common/input/selectItemV2";

type Props = {
  seats: Seat[];
  seatTypes: SeatType[];
  prices: Price[];
  onClose: () => void;
  onSubmit: (seats: Seat[]) => Promise<void>;
  stageId: number;
  device?: Device;
  reservationStarted: boolean;
};

const operations: { value: number; name: string; }[] = [
  {
    value: 0,
    name: "座席種類変更",
  },
  {
    value: 1,
    name: "座席名変更",
  },
  {
    value: 2,
    name: "座席ロック",
  },
  {
    value: 3,
    name: "csvから座席作成"
  }
]

const EditSeat = (props: Props) => {
  const operations: { value: number; name: string; }[] = useMemo(() => props.reservationStarted ? [
    {
      value: 0,
      name: "座席種類変更",
    },
    {
      value: 1,
      name: "座席名変更",
    },
    {
      value: 2,
      name: "座席ロック",
    },
  ] : [
    {
      value: 0,
      name: "座席種類変更",
    },
    {
      value: 1,
      name: "座席名変更",
    },
    {
      value: 2,
      name: "座席ロック",
    },
    {
      value: 3,
      name: "csvから座席作成"
    }
  ], [props.reservationStarted])
  
  const snackBar = useSnackBar({})
  const [seats, setSeats] = useState([...props.seats]);

  const { row, column } = useMemo(() => {
    if(!seats.length) {
      return {
        row: 0,
        column: 0,
      }
    }
    const tree: { [row: string]: { [column: string]: number } } = {}
    seats.forEach((seat, index) => {
      if(!tree[seat.row]) {
        tree[seat.row] = {}
      }
      tree[seat.row][seat.column] = index
    })
    const row = Object.keys(tree).length
    const column = Object.keys(tree[1])?.length ?? 0

    return {
      row,
      column,
    }
  }, [seats])

  const getRowAndColumn = useCallback((seats: Seat[]) => {
    if(!seats.length) {
      return {
        row: 0,
        column: 0,
      }
    }
    const tree: { [row: string]: { [column: string]: number } } = {}
    seats.forEach((seat, index) => {
      if(!tree[seat.row]) {
        tree[seat.row] = {}
      }
      tree[seat.row][seat.column] = index
    })
    const row = Object.keys(tree).length
    const column = Object.keys(tree[1])?.length ?? 0

    return {
      row,
      column,
    }
  }, [])

  const { initialRow, initialColumn } = useMemo(() => {
    const tree: { [row: string]: { [column: string]: number } } = {}
    props.seats.forEach((seat, index) => {
      if(!tree[seat.row]) {
        tree[seat.row] = {}
      }
      tree[seat.row][seat.column] = index
    })
    const row = Object.keys(tree).length
    const column = Object.keys(tree[1]).length

    return {
      initialRow: row,
      initialColumn: column,
    }
  }, [props.seats])

  const [inputRow, setInputRow] = useState(undefined as undefined | number);
  const [inputCol, setInputCol] = useState(undefined as undefined | number);
  const [rowError, setRowError] = useState(false);
  const [colError, setColError] = useState(false);

  const [operation, setOperation] = useState(0)
  const [mode, setMode] = useState(props.seatTypes[0].id);

  const [selectedSeat, setSelectedSeat] = useState<Seat | undefined>(undefined)
  const [inputSeatName, setInputSeatName] = useState("")

  const [csv, setCsv] = useState<FileList | null>()

  const onRowColChanged = useCallback((newSeats: Seat[]) => {
    console.log(newSeats);
    setSeats(newSeats);
  }, []);

  const colorRule = useCallback((seat: Seat) => {
    if(seat.reservationId && seat.reservationId > 0) {
      return "gray"
    }
    return undefined
  }, [])

  if(csv && csv[0]) {
    console.log(csv.length)
    const reader = new FileReader()
    reader.onloadend = () => {
      console.log(reader.result)
      const result = reader.result
      if(result && typeof result === "string") {
        const resultLineReplaced = result.replaceAll("\r\n", "\r").replaceAll("\r", "\n")
        const array = resultLineReplaced.split("\n")

        console.log(array)
      }
    }
    reader.readAsText(csv[0])
  }

  const [createSeatsFromCsv] = GraphQLClient.useMutation(CreateSeatsFromCsv, {
    onCompleted: (data) => {
      if(data.createSeatsFromCsv.ok) {
        snackBar.onMutationSuccess()
      } else {
        snackBar.onMutationError()
      }
    },
    onError: (err) => {
      console.log(err)
      snackBar.onMutationError()
    }
  })

  const submitCsv = useCallback(async () => {
    if(csv && csv[0]) {
      const reader = new FileReader()
      reader.onloadend = async () => {
        const result = reader.result
        await createSeatsFromCsv({
          variables: {
            input: {
              stageId: props.stageId,
              object: result
            }
          }
        })
      }
      reader.readAsText(csv[0])
    }
  }, [createSeatsFromCsv, csv, props.stageId])

  const selectedOperation = useMemo(() => {
    return operations.findIndex(o => o.value === operation)
  }, [operation])

  const operationSelect = useMemo(() => {
    return (
        <div className="selector">
            <InputLabel>編集内容選択</InputLabel>
            <SelectItemV2 
              items={operations}
              selectedIndex={selectedOperation}
              changeIndex={(item, _) => { setOperation(item.value) }}
              width={250}
            />
        </div>
    )
  }, [selectedOperation])

  const onSelected = useCallback((seatType: SeatType) => {
    setMode(seatType.id);
  }, []);
  const selectedSeatTypeIndex = useMemo(() => {
    return props.seatTypes.findIndex(st => st.id === mode)
  }, [mode, props.seatTypes])
  const seatTypeSelect = useMemo(() => {
    return (
      <div className="selector">
        <InputLabel>座席種類選択</InputLabel>
        <SelectItemV2 
          items={props.seatTypes}
          selectedIndex={selectedSeatTypeIndex}
          changeIndex={(item, _) => { onSelected(item) }}
          width={250}
        />
      </div>
    );
  }, [onSelected, props.seatTypes, selectedSeatTypeIndex]);

  const onSeatNameEntered = useCallback(() => {
    if(!selectedSeat) {
      return
    }
    const index = seats.findIndex((seat) => seat.row === selectedSeat.row && seat.column === selectedSeat.column)
    const seatsCopy = [...seats]
    seatsCopy[index] = {...seatsCopy[index], seatName: inputSeatName}
    setSeats(seatsCopy)
  }, [inputSeatName, seats, selectedSeat])
  const seatNameInvalid = useMemo(() => {
    const texts: string[] = []
    if(!inputSeatName) {
      texts.push("座席名は必須です")
    } else {
      if(inputSeatName.length > 10) {
        texts.push("座席名は10文字以内です")
      }
    }
    return texts
  }, [inputSeatName])

  const editSeatName = useMemo(() => {
    return selectedSeat ? (
      <div className="edit-seat-name">
        <TextField 
          defaultValue={selectedSeat.seatName}
          key={`${selectedSeat.row}-${selectedSeat.column}-${selectedSeat.seatName}`}
          onChange={(event) => { setInputSeatName(event.target.value) }}
          error={seatNameInvalid.length > 0}
          helperText={seatNameInvalid}
          variant="standard"
        />
        <Button 
          buttonType="powerful-red"
          onClick={onSeatNameEntered}
          label="変更"
          disabled={seatNameInvalid.length > 0}
        />
      </div>
    ) : (
      <div className="edit-seat-name">
        <p>名前を変更する座席を選択してください</p>
      </div>
    )
  }, [onSeatNameEntered, seatNameInvalid, selectedSeat])
  useQuery([selectedSeat], () => {
    setInputSeatName("")
  })

  useQuery([operation], () => {
    setCsv(undefined)
  })

  const readCsv = useMemo(() => {
    return (
      <div className="read-csv">
        <div className="csv-input">
          <input 
            type="file"
            onChange={(event) => { setCsv(event.target.files) }}
            name="ファイル選択"
          />
        </div>
        <Button 
          onClick={submitCsv}
          buttonType="powerful-red"
          label="作成"
          disabled={!csv}
        />
      </div>
    )
  }, [csv, submitCsv])

  // 行列数増減

  const onRowChanged = useCallback(
    (newRowStr: string, errorOccurs: boolean) => {
      setRowError(errorOccurs);
      if (!errorOccurs) {
        setInputRow(parseInt(newRowStr));
      }
    },
    [],
  );

  const onColChanged = useCallback(
    (newColStr: string, errorOccurs: boolean) => {
      setColError(errorOccurs);
      if (!errorOccurs) {
        setInputCol(parseInt(newColStr));
      }
    },
    [],
  );

  const applyInput = useCallback(() => {
    let seatsCopy = [...seats]

    const applyRowChange = () => {
      if (rowError || !inputRow) {
        return;
      }

      const gap = inputRow - row;
      if (gap === 0) {
        return;
      }
      const ro = getRowAndColumn(seatsCopy).row
      const col = getRowAndColumn(seatsCopy).column
      if (gap < 0) {
        for(let i = ro; i > ro + gap; i--) {
          seatsCopy = seatsCopy.filter((seat) => {
            return seat.row !== i
          })
        }
        return
      }
      if (gap > 0) {
        for (let i = 1; i <= gap; i++) {
          for (let j = 1; j <= col; j++) {
            seatsCopy.push({
              column: j,
              row: row + i,
              stageId: props.stageId,
              seatName: `${numberToCharacter[row + i]}-${j}`,
              type: 0,
            });
          }
        }
        return
      }
    };

    const applyColChange = () => {
      if (colError || !inputCol) {
        return;
      }
      const gap = inputCol - column;
      if (gap === 0) {
        return;
      }
      if (gap < 0) {
        for(let i = column; i > column + gap; i--) {
          seatsCopy = seatsCopy.filter((seat) => {
            return seat.column !== i
          })
        }
        return
      }
      if (gap > 0) {
        const ro = getRowAndColumn(seatsCopy).row
        const col = getRowAndColumn(seatsCopy).column
        for (let i = 1; i <= gap; i++) {
          for(let j = 1; j <= ro; j++) {
            seatsCopy.push({
              column: col + i,
              row: j,
              stageId: props.stageId,
              seatName: `${numberToCharacter[j]}-${col + i}`,
              type: 0,
            });
          }
        }
        return
      }
    };

    applyRowChange();
    applyColChange();
    onRowColChanged(seatsCopy);
  }, [colError, column, getRowAndColumn, inputCol, inputRow, onRowColChanged, props.stageId, row, rowError, seats]);

  
  // falseを返すと、処理後に座席選択が解除される
  const onSeatSelected = useCallback((seat: Seat | undefined) => {
    setSelectedSeat(seat)
    if(!seat) {
      return true
    }
    if(seat.reservationId && seat.reservationId > 0) {
      return true
    }

    // operationに応じて処理変更
    if(operation === 0) {
      const index = seats.findIndex(item => item.column === seat.column && item.row === seat.row)
      if(index >= 0) {
        const seatsCopy = [...seats]
        seatsCopy[index] = {...seatsCopy[index], type: mode}
        setSeats(seatsCopy)
        return false
      }
    }

    if(operation === 2) {
      const index = seats.findIndex(item => item.column === seat.column && item.row === seat.row)
      if(index >= 0) {
        const seatsCopy = [...seats]
        seatsCopy[index] = {...seatsCopy[index], locked: !(seatsCopy[index].locked ?? false)}
        setSeats(seatsCopy)
        return false
      }
    }
    return true
  }, [mode, operation, seats])

  const submit = useCallback(async () => {
    console.log(seats.filter(seat => seat.type === 5))
    await props.onSubmit(seats);
  }, [props, seats]);

  const deviceType = useMemo(() => {
    if (
      props.device === "ios" ||
      props.device === "Android" ||
      props.device === "Mobile"
    ) {
      return "Mobile";
    } else {
      return "PC";
    }
  }, [props.device]);

  const description = useMemo(() => {
    if(operation === 0) {
      return "座席をタップ/クリックすると、選択中の座席種類に変更できます。"
    }
    if(operation === 1) {
      return "選択した座席の名称を変更できます。"
    }
  }, [operation])

  const lockRule = useCallback((seat: Seat) => {
    if(seat.reservationId && seat.reservationId > 0) {
      return true
    }
    return false
  }, [])

  

  const components = useMemo(() => {
    const body = (
      <>
        <div className="description">
          {description}
        </div>
        <div className="select">
          {operationSelect}
          {operation === 0 && seatTypeSelect}
          {operation === 1 && editSeatName}
          {operation === 3 && !props.reservationStarted && readCsv}
        </div>
        <div className="seats">
          <SeatsDisplay 
            seats={seats}
            colorRule={colorRule}
            onSeatClicked={onSeatSelected}
            seatTypes={props.seatTypes}
            size="small"
            lockRule={lockRule}
          />
        </div>
        {!props.reservationStarted && <div className="row-col-select">
          <GuestInput
            id="seats-edit-row"
            label="行"
            error={rowError}
            errorMessage="無効な数値です"
            defaultValue={String(initialColumn)}
            placeholder="行数"
            literalValidation={/^[1-9][0-9]*$/}
            numeralValidation={{ max: 30, min: 0 }}
            onChange={onRowChanged}
            width={50}
          />
          <GuestInput
            id="seats-edit-column"
            label="列"
            error={colError}
            errorMessage="無効な数値です"
            defaultValue={
              String(initialRow)
            }
            placeholder="列数"
            literalValidation={/^[1-9][0-9]*$/}
            numeralValidation={{ max: 30, min: 0 }}
            onChange={onColChanged}
            width={50}
          />
          <div className="button">
            <Button
              onClick={applyInput}
              label="適用"
              buttonType={isMobile() ? "default-red" : "powerful-red"}
              width={80}
            />
          </div>
        </div>}
        <div className="buttons">
          <div className="button">
            <Button
              onClick={submit}
              label="保存"
              buttonType={isMobile() ? "default-red" : "powerful-red"}
              width={80}
            />
          </div>
          <div className="button">
            <Button
              onClick={props.onClose}
              label="キャンセル"
              buttonType="back"
              width={120}
            />
          </div>
        </div>
      </>
    );
    return deviceType === "Mobile" ? (
      <Modal title="座席編集" onClose={props.onClose} width={350} maxHeight={800}>
        <EditSeatStyleMobile>{body}</EditSeatStyleMobile>
      </Modal>
    ) : (
      <Modal
        title="座席編集"
        onClose={props.onClose}
        width={1000}
        maxHeight={800}
      >
        <EditSeatStylePC>{body}</EditSeatStylePC>
      </Modal>
    );
  }, [applyInput, colError, colorRule, description, deviceType, editSeatName, initialColumn, initialRow, lockRule, onColChanged, onRowChanged, onSeatSelected, operation, operationSelect, props.onClose, props.reservationStarted, props.seatTypes, readCsv, rowError, seatTypeSelect, seats, submit]);
  return <>
    {components}
    {snackBar.component}
  </>;
};

export default EditSeat;

const EditSeatStyleMobile = styled.div`
  .select {
    margin: auto;
    text-align: center;
  }
  .seats {
    margin: 16px auto 16px auto;
    width: 320px;
  }
  .row-col-select {
    width: 240px;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .buttons {
    display: flex;
    margin: 16px auto 16px auto;
    justify-content: center;
    .button {
      margin: 0 4px 0 4px;
    }
  }
`;

const EditSeatStylePC = styled.div`
  .select {
    margin: auto;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    height: 100px;
    .selector {
      width: 250px;
    }
    .edit-seat-name {}
  }
  .seats {
    margin: 16px auto 16px auto;
    width: 800px;
    height: 400px;
    overflow: scroll;
  }
  .row-col-select {
    width: 240px;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .buttons {
    display: flex;
    margin: 16px auto 16px auto;
    justify-content: center;
    .button {
      margin: 0 4px 0 4px;
    }
  }
  .read-csv {
    .csv-input {
      margin-bottom: 8px;
    }
  }
`;
